import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stack,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Alarm as AlarmIcon,
  Repeat as RepeatIcon,
  Chat as ChatIcon,
  Email as EmailIcon,
  Notifications as PushIcon
} from '@mui/icons-material';
import {
  Reminder,
  CreateReminderDTO,
  UpdateReminderDTO,
  ReminderPriority,
  ReminderType,
  RecurrencePattern,
  NotificationChannel
} from '../../services/api/todo.service';

interface ReminderDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (reminder: CreateReminderDTO | UpdateReminderDTO) => void;
  existingReminder?: Reminder | null;
  taskId?: string;
  conversationId?: string;
}

const ReminderDialog: React.FC<ReminderDialogProps> = ({
  open,
  onClose,
  onSave,
  existingReminder,
  taskId,
  conversationId
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [type, setType] = useState<ReminderType>('one_time');
  const [priority, setPriority] = useState<ReminderPriority>('medium');
  const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>(['chat']);
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern>('daily');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceDaysOfWeek, setRecurrenceDaysOfWeek] = useState<number[]>([]);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [maxOccurrences, setMaxOccurrences] = useState<number | undefined>();

  useEffect(() => {
    if (existingReminder) {
      setTitle(existingReminder.title);
      setDescription(existingReminder.description || '');
      setReminderTime(existingReminder.reminder_time.slice(0, 16)); // Format for datetime-local
      setType(existingReminder.type);
      setPriority(existingReminder.priority);
      setNotificationChannels(existingReminder.notification_channels as NotificationChannel[]);
      setRecurrencePattern(existingReminder.recurrence_pattern || 'daily');
      setRecurrenceInterval(existingReminder.recurrence_interval || 1);
      setRecurrenceDaysOfWeek(existingReminder.recurrence_days_of_week || []);
      setRecurrenceEndDate(existingReminder.recurrence_end_date || '');
      setMaxOccurrences(existingReminder.max_occurrences);
    } else {
      // Reset to defaults for new reminder
      setTitle('');
      setDescription('');
      setReminderTime('');
      setType('one_time');
      setPriority('medium');
      setNotificationChannels(['chat']);
      setRecurrencePattern('daily');
      setRecurrenceInterval(1);
      setRecurrenceDaysOfWeek([]);
      setRecurrenceEndDate('');
      setMaxOccurrences(undefined);
    }
  }, [existingReminder, open]);

  const handleSave = () => {
    const reminderData: CreateReminderDTO | UpdateReminderDTO = {
      title,
      description: description || undefined,
      reminder_time: new Date(reminderTime).toISOString(),
      type,
      priority,
      notification_channels: notificationChannels,
      ...(taskId && { task_id: taskId }),
      ...(conversationId && { conversation_id: conversationId }),
      ...(type === 'recurring' && {
        recurrence_pattern: recurrencePattern,
        recurrence_interval: recurrenceInterval,
        ...(recurrencePattern === 'weekly' && { recurrence_days_of_week: recurrenceDaysOfWeek }),
        ...(recurrenceEndDate && { recurrence_end_date: recurrenceEndDate }),
        ...(maxOccurrences && { max_occurrences: maxOccurrences })
      })
    };

    onSave(reminderData);
  };

  const handleChannelToggle = (channel: NotificationChannel) => {
    setNotificationChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };

  const handleDayOfWeekToggle = (day: number) => {
    setRecurrenceDaysOfWeek(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const getDayName = (day: number) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[day];
  };

  const getPriorityColor = (p: ReminderPriority) => {
    switch (p) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const getChannelIcon = (channel: NotificationChannel) => {
    switch (channel) {
      case 'chat': return <ChatIcon fontSize="small" />;
      case 'email': return <EmailIcon fontSize="small" />;
      case 'push': return <PushIcon fontSize="small" />;
      default: return <ChatIcon fontSize="small" />;
    }
  };

  const isValid = () => {
    if (!title.trim()) return false;
    if (!reminderTime) return false;
    if (new Date(reminderTime) <= new Date()) return false;
    if (notificationChannels.length === 0) return false;
    if (type === 'recurring' && recurrencePattern === 'weekly' && recurrenceDaysOfWeek.length === 0) return false;
    return true;
  };

  const getRecurrenceDescription = () => {
    if (type !== 'recurring') return '';

    switch (recurrencePattern) {
      case 'daily':
        return `Every ${recurrenceInterval} day${recurrenceInterval > 1 ? 's' : ''}`;
      case 'weekly':
        const dayNames = recurrenceDaysOfWeek.map(d => getDayName(d)).join(', ');
        return `Every ${recurrenceInterval} week${recurrenceInterval > 1 ? 's' : ''} on ${dayNames || 'no days selected'}`;
      case 'monthly':
        return `Every ${recurrenceInterval} month${recurrenceInterval > 1 ? 's' : ''}`;
      case 'yearly':
        return `Every ${recurrenceInterval} year${recurrenceInterval > 1 ? 's' : ''}`;
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AlarmIcon color="primary" />
          <Typography variant="h6">
            {existingReminder ? 'Edit Reminder' : 'Create Reminder'}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} style={{ marginTop: 8 }}>
          {/* Title */}
          <TextField
            label="Reminder Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            placeholder="What should we remind you about?"
          />

          {/* Description */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Additional details (optional)"
          />

          {/* Reminder Time */}
          <TextField
            label="Reminder Time"
            type="datetime-local"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: new Date().toISOString().slice(0, 16)
            }}
          />

          {/* Type and Priority */}
          <Stack direction="row" spacing={2}>
            <FormControl style={{ flex: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value as ReminderType)}
                label="Type"
              >
                <MenuItem value="one_time">One Time</MenuItem>
                <MenuItem value="recurring">Recurring</MenuItem>
                <MenuItem value="conditional">Conditional</MenuItem>
              </Select>
            </FormControl>

            <FormControl style={{ flex: 1 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value as ReminderPriority)}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Notification Channels */}
          <div>
            <Typography variant="subtitle2" gutterBottom>
              Notification Channels
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(['chat', 'email', 'push'] as NotificationChannel[]).map(channel => (
                <Chip
                  key={channel}
                  icon={getChannelIcon(channel)}
                  label={channel.charAt(0).toUpperCase() + channel.slice(1)}
                  variant={notificationChannels.includes(channel) ? 'filled' : 'outlined'}
                  color={notificationChannels.includes(channel) ? 'primary' : 'default'}
                  onClick={() => handleChannelToggle(channel)}
                  clickable
                />
              ))}
            </Stack>
          </div>

          {/* Recurring Options */}
          {type === 'recurring' && (
            <div style={{ padding: 16, backgroundColor: '#fafafa', borderRadius: 4 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RepeatIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2">
                    Recurrence Settings
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={2}>
                  <FormControl style={{ flex: 1 }}>
                    <InputLabel>Pattern</InputLabel>
                    <Select
                      value={recurrencePattern}
                      onChange={(e) => setRecurrencePattern(e.target.value as RecurrencePattern)}
                      label="Pattern"
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Every X"
                    type="number"
                    value={recurrenceInterval}
                    onChange={(e) => setRecurrenceInterval(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ flex: 1 }}
                    inputProps={{ min: 1 }}
                  />
                </Stack>

                {/* Weekly Days */}
                {recurrencePattern === 'weekly' && (
                  <div>
                    <Typography variant="body2" gutterBottom>
                      Days of the Week
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {[0, 1, 2, 3, 4, 5, 6].map(day => (
                        <Chip
                          key={day}
                          label={getDayName(day)}
                          variant={recurrenceDaysOfWeek.includes(day) ? 'filled' : 'outlined'}
                          color={recurrenceDaysOfWeek.includes(day) ? 'primary' : 'default'}
                          onClick={() => handleDayOfWeekToggle(day)}
                          clickable
                          size="small"
                        />
                      ))}
                    </Stack>
                  </div>
                )}

                {/* End Conditions */}
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={recurrenceEndDate}
                    onChange={(e) => setRecurrenceEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    style={{ flex: 1 }}
                  />

                  <TextField
                    label="Max Occurrences"
                    type="number"
                    value={maxOccurrences || ''}
                    onChange={(e) => setMaxOccurrences(e.target.value ? parseInt(e.target.value) : undefined)}
                    inputProps={{ min: 1 }}
                    style={{ flex: 1 }}
                  />
                </Stack>

                {/* Pattern Preview */}
                <Typography variant="body2" color="primary">
                  {getRecurrenceDescription()}
                </Typography>
              </Stack>
            </div>
          )}

          {/* Priority Preview */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography variant="body2">Priority:</Typography>
            <Chip
              label={priority.charAt(0).toUpperCase() + priority.slice(1)}
              color={getPriorityColor(priority) as any}
              size="small"
            />
          </div>

          {/* Validation Warnings */}
          {!isValid() && (
            <Alert severity="warning">
              {!title.trim() && 'Title is required'}
              {!reminderTime && 'Reminder time is required'}
              {reminderTime && new Date(reminderTime) <= new Date() && 'Reminder time must be in the future'}
              {notificationChannels.length === 0 && 'At least one notification channel is required'}
              {type === 'recurring' && recurrencePattern === 'weekly' && recurrenceDaysOfWeek.length === 0 &&
                'Please select at least one day for weekly recurrence'}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid()}
          startIcon={<AlarmIcon />}
        >
          {existingReminder ? 'Update Reminder' : 'Create Reminder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReminderDialog;